// ProposalDetail — modal with full proposal info + EIP-712 gas-free voting.
// Must be rendered INSIDE a WagmiProvider (GovernancePage provides it).
import React, { useCallback, useEffect, useState } from "react";
import { useAccount, useSignTypedData, useSwitchChain } from "wagmi";
import { X, Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tGov } from "../../i18n/governance";
import { buildVoteTypedData } from "../../web3/governanceSig";
import { BASE_CHAIN_ID } from "../../web3/wagmiConfig";
import { VoteBars, StatusPill, shortWallet } from "./ProposalCard";

const fmt = (n) => Number(n).toLocaleString();

// Minimal markdown: bold, line breaks, "- " bullets. No external deps.
const MdText = ({ text }) => (
  <div className="space-y-1 text-sm leading-relaxed text-white/70">
    {(text || "").split("\n").map((line, i) => {
      const parts = line.split(/\*\*(.+?)\*\*/g).map((seg, j) =>
        j % 2 === 1 ? <b key={j} className="text-white">{seg}</b> : seg
      );
      if (line.startsWith("- ")) {
        return (
          <p key={i} className="flex gap-2">
            <span className="text-purple-300">•</span>
            <span>{line.slice(2) && parts.map((s, j) => (j === 0 && typeof s === "string" ? s.replace(/^- /, "") : s))}</span>
          </p>
        );
      }
      return line.trim() === "" ? <div key={i} className="h-2" /> : <p key={i}>{parts}</p>;
    })}
  </div>
);

const Countdown = ({ endsAt, t }) => {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = new Date(endsAt) - Date.now();
  if (ms <= 0) return <span>{t("closed")}</span>;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return (
    <span className="font-mono font-black text-cyan-300" data-testid="gov-detail-countdown">
      {d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s
    </span>
  );
};

export default function ProposalDetail({ proposalId, power, onClose, onVoted }) {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  const { user, token, openLogin } = useAuth();
  const { address, isConnected, chainId } = useAccount();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();
  const wrongNetwork = isConnected && chainId !== BASE_CHAIN_ID;

  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(null); // 'yes' | 'no'
  const [error, setError] = useState(null);
  const [voted, setVoted] = useState(null);

  const load = useCallback(() => {
    const q = address ? `?wallet=${address}` : "";
    fetch(`${API}/governance/proposals/${proposalId}${q}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("load_failed"))))
      .then(setP)
      .catch(() => setError("load_failed"));
  }, [proposalId, address]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const castVote = async (vote) => {
    setError(null);
    setBusy(vote);
    try {
      const { typedData, timestamp } = buildVoteTypedData(proposalId, vote, address);
      const signature = await signTypedDataAsync(typedData);
      const r = await fetch(`${API}/governance/vote`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          proposal_id: proposalId, vote,
          wallet_address: address, signature, timestamp,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.error || "vote_failed");
      setVoted({ vote, voting_power: d.voting_power });
      load();
      onVoted?.();
    } catch (e) {
      const code = e?.message;
      setError(tGov(lang, `err_${code}`) !== `err_${code}` ? tGov(lang, `err_${code}`) : (e?.shortMessage || code));
    } finally {
      setBusy(null);
    }
  };

  const myVote = voted || p?.my_vote;
  const isActive = p?.status === "active";

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm md:items-center"
      onClick={onClose} data-testid="gov-detail-overlay">
      <div className="w-full max-w-2xl rounded-3xl border border-white/15 bg-[#0B0F19] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="gov-detail-modal">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {p && <StatusPill p={p} />}
            {p && isActive && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-white/55">
                <Clock className="h-3.5 w-3.5" /> {t("closesIn")} <Countdown endsAt={p.ends_at} t={t} />
              </span>
            )}
          </div>
          <button onClick={onClose} data-testid="gov-detail-close" className="rounded-full p-1.5 text-white/40 hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!p ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-purple-300" /></div>
        ) : (
          <>
            <h2 className="mt-3 text-2xl font-black text-white">{p.title}</h2>
            <p className="mt-1 text-[11px] text-white/45">
              {t("proposedBy")}: <span className="font-mono">{shortWallet(p.proposer_wallet) || t("team")}</span>
              <span className="ml-2 rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] uppercase text-white/50">{t(`type_${p.type}`)}</span>
              {p.snapshot_block && <span className="ml-2 font-mono text-[9px] text-white/30">snapshot #{p.snapshot_block}</span>}
            </p>

            <div className="mt-4 max-h-60 overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <MdText text={p.description} />
            </div>

            <div className="mt-5">
              <VoteBars p={p} />
              <p className="mt-2 font-mono text-[10px] text-white/40">
                {t("quorum")}: {p.quorum_pct}% ({fmt(p.total_votes)} / {fmt(p.quorum_rbl)} R₿L) ·{" "}
                <span className={p.quorum_met ? "text-emerald-300" : "text-white/40"}>
                  {p.quorum_met ? t("quorumMet") : t("quorumNotMet")}
                </span>
              </p>
            </div>

            {/* Voting zone */}
            <div className="mt-5 rounded-2xl border border-purple-400/25 bg-purple-400/[0.04] p-4" data-testid="gov-vote-zone">
              {myVote ? (
                <div className="flex items-center gap-3" data-testid="gov-already-voted">
                  <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-400" />
                  <div>
                    <p className="text-sm font-black text-white">
                      {t("yourVote")}: {myVote.vote === "yes" ? `✅ ${t("yes")}` : `❌ ${t("no")}`}
                    </p>
                    <p className="text-[11px] text-white/55">{t("yourPowerUsed")}: {fmt(myVote.voting_power)} R₿L</p>
                  </div>
                </div>
              ) : !isActive ? (
                <p className="text-xs text-white/45">{t("closed")} · {p.close_reason || ""}</p>
              ) : !user ? (
                <button onClick={openLogin} data-testid="gov-vote-login"
                  className="w-full rounded-xl border border-purple-400/40 bg-purple-400/10 py-3 text-xs font-black uppercase tracking-[0.18em] text-purple-200 hover:bg-purple-400/20">
                  {t("loginFirst")}
                </button>
              ) : !isConnected ? (
                <p className="text-center text-xs text-white/55">{t("connectToVote")} ↑</p>
              ) : wrongNetwork ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-300">
                    <AlertTriangle className="h-3.5 w-3.5" /> {t("wrongNet")}
                  </span>
                  <button onClick={() => switchChain({ chainId: BASE_CHAIN_ID })} disabled={isSwitching}
                    className="rounded-full bg-amber-400 px-3 py-1.5 text-[11px] font-black uppercase text-black disabled:opacity-50">
                    {t("switchBase")}
                  </button>
                </div>
              ) : power && !power.can_vote ? (
                <p className="text-center text-xs text-rose-300" data-testid="gov-insufficient-balance">{t("minBalanceVote")}</p>
              ) : (
                <>
                  <p className="text-[11px] text-white/55">
                    {t("votingAs")}: <span className="font-mono text-white">{address?.slice(0, 6)}…{address?.slice(-4)}</span>
                    {power && <> · {fmt(power.votes)} {t("votes")}</>}
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <button onClick={() => castVote("yes")} disabled={!!busy} data-testid="gov-vote-yes"
                      className="rounded-xl bg-emerald-400 py-3 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-40">
                      {busy === "yes" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : `✅ ${t("yes")}`}
                    </button>
                    <button onClick={() => castVote("no")} disabled={!!busy} data-testid="gov-vote-no"
                      className="rounded-xl bg-rose-400 py-3 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-40">
                      {busy === "no" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : `❌ ${t("no")}`}
                    </button>
                  </div>
                  <p className="mt-2 text-center text-[10px] text-white/35">{t("freeVoteNote")}</p>
                </>
              )}
              {error && (
                <p className="mt-2 break-words rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[11px] text-rose-300" data-testid="gov-vote-error">
                  {error}
                </p>
              )}
            </div>

            {/* Recent voters */}
            <div className="mt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">{t("recentVoters")}</p>
              {(p.recent_voters || []).length === 0 ? (
                <p className="mt-2 text-xs text-white/35">{t("noVotersYet")}</p>
              ) : (
                <ul className="mt-2 max-h-44 divide-y divide-white/5 overflow-y-auto rounded-xl border border-white/10">
                  {p.recent_voters.map((v, i) => (
                    <li key={i} className="flex items-center justify-between px-3 py-2 text-[11px]" data-testid={`gov-voter-${i}`}>
                      <span className="font-mono text-white/60">{shortWallet(v.wallet)}</span>
                      <span className={`font-bold ${v.vote === "yes" ? "text-emerald-300" : "text-rose-300"}`}>
                        {v.vote === "yes" ? t("yes") : t("no")}
                      </span>
                      <span className="font-mono text-white/45">{fmt(v.voting_power)} R₿L</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
